/**
 * AppErrorBoundary - Sentry-aware Error Boundary
 *
 * Catches rendering errors, displays a Piccard styled fallback UI,
 * and reports errors to Sentry/Better Stack.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  eventId: string | null
}

export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, eventId: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })
    this.setState({ eventId })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, eventId: null })
  }

  handleReport = (): void => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId })
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="error-boundary"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            backgroundColor: 'var(--color-surface, #faf9f5)',
            borderRadius: 'var(--radius-xl, 24px)',
            fontFamily: 'var(--font-body, "Inter", system-ui, sans-serif)',
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--color-surface-soft, #f5f0e8)',
              borderRadius: 'var(--radius-lg, 20px)',
              padding: '2rem',
              maxWidth: '400px',
              textAlign: 'center',
              boxShadow: 'var(--shadow-card, 0px 4px 12px 0px rgba(15, 15, 15, 0.08))',
            }}
          >
            <h2
              style={{
                color: 'var(--color-error, #c64545)',
                fontFamily: 'var(--font-headline, "Newsreader", Georgia, serif)',
                marginBottom: '0.75rem',
                fontSize: '1.25rem',
              }}
            >
              Something went wrong
            </h2>
            <p
              style={{
                color: 'var(--color-on-surface-variant, #6c6a64)',
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={this.handleRetry}
                style={{
                  backgroundColor: 'var(--color-primary, #cc785c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md, 12px)',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: '120px',
                }}
              >
                Try Again
              </button>
              <button
                onClick={this.handleReport}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--color-on-surface-variant, #6c6a64)',
                  border: '2px solid var(--color-hairline, #e6dfd8)',
                  borderRadius: 'var(--radius-md, 12px)',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: '120px',
                }}
              >
                Report
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
